import React, { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import Alert from '../../../../../shared/components/Alert/Alert';
import Loader from '../../../../../shared/components/Loader/Loader';
import { wikiEditorExtensions } from '../../extensions/wikiEditorExtensions';
import { normalizeTiptapDocument } from '../../services/wikiContentSerializer';
import WikiEditorStatus from './WikiEditorStatus';
import WikiEditorToolbar from './WikiEditorToolbar';

function WikiTiptapEditor({
  autosave,
  error,
  onChange,
  readOnly = false,
  value,
}) {
  const extensions = useMemo(() => wikiEditorExtensions(), []);
  const initialContent = useRef(normalizeTiptapDocument(value));
  const editor = useEditor({
    extensions,
    content: initialContent.current,
    editable: !readOnly,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange?.(currentEditor);
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor || editor.isFocused) {
      return;
    }

    const nextContent = normalizeTiptapDocument(value);
    const currentContent = editor.getJSON();
    if (JSON.stringify(nextContent) !== JSON.stringify(currentContent)) {
      editor.commands.setContent(nextContent, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <Loader className="wiki-tiptap-loading" label="Chargement de l'editeur..." size="large" />
    );
  }

  return (
    <div className={readOnly ? 'wiki-tiptap-shell readonly' : 'wiki-tiptap-shell'}>
      {!readOnly && <WikiEditorToolbar editor={editor} />}
      {error && <Alert severity="error">{error}</Alert>}
      <div className="wiki-tiptap-content">
        <EditorContent editor={editor} />
      </div>
      {!readOnly && (
        <WikiEditorStatus
          characterCount={editor.storage.characterCount?.characters() || 0}
          error={autosave?.error}
          savedAt={autosave?.savedAt}
          state={autosave?.state}
        />
      )}
    </div>
  );
}

export default WikiTiptapEditor;
